import {
  Alert,
  Disclosure,
  DisclosureGroup,
  Heading,
  Text,
  Timeline,
  buttonVariants,
} from "@/design-system";
import { cn } from "@/utils";
import { Mail } from "lucide-react";
import type { LegalBlock } from "../content/types";
import { LegalRichText } from "./legalRichText";
import { LegalTableView } from "./legalTableView";

type LegalBlockViewProps = {
  block: LegalBlock;
};

/** Um bloco do texto completo de uma seção. */
export const LegalBlockView = ({ block }: LegalBlockViewProps) => {
  switch (block.type) {
    case "paragraph":
      return (
        <Text tone="muted">
          <LegalRichText text={block.text} />
        </Text>
      );

    case "subheading":
      return (
        <Heading level={3} className="pt-2">
          {block.text}
        </Heading>
      );

    case "list": {
      const ListTag = block.ordered ? "ol" : "ul";
      return (
        <ListTag
          className={
            block.ordered
              ? "marker:text-ds-brand-700 flex list-decimal flex-col gap-2 pl-6 marker:font-semibold"
              : "marker:text-ds-brand-600 flex list-disc flex-col gap-2 pl-6"
          }
        >
          {block.items.map(item => (
            <Text as="li" key={item} tone="muted" className="pl-1">
              <LegalRichText text={item} />
            </Text>
          ))}
        </ListTag>
      );
    }

    case "table":
      return <LegalTableView table={block} />;

    case "action":
      return (
        <a
          href={block.href}
          className={cn(
            buttonVariants({
              variant: "primary",
              size: "md",
              fullWidth: false,
            }),
            "sm:self-start"
          )}
        >
          {block.href.startsWith("mailto:") && <Mail aria-hidden="true" />}
          {block.label}
        </a>
      );

    case "timeline":
      return (
        <Timeline
          label={block.caption}
          items={block.items.map(item => ({
            time: item.time,
            tone: item.tone,
            children: <LegalRichText text={item.text} />,
          }))}
        />
      );

    case "faq":
      // A pergunta é h3: a seção da página já é h2.
      return (
        <DisclosureGroup>
          {block.items.map(item => (
            <Disclosure
              key={item.question}
              summary={item.question}
              headingLevel={3}
            >
              <LegalRichText text={item.answer} />
            </Disclosure>
          ))}
        </DisclosureGroup>
      );

    case "callout":
      return (
        <Alert tone={block.tone} title={block.title}>
          <LegalRichText text={block.text} />
        </Alert>
      );
  }
};
